import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { X, ChevronRight, Mail, Phone, Users, Loader2, Search as SearchIcon } from "lucide-react";

// Read-only floating popup replica of legacy Forms_BusinessContacts.
export default function BusinessContacts({ onClose }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState("");          // selected company name
  const [contactId, setContactId] = useState(null);
  const [nameFilter, setNameFilter] = useState("");

  useEffect(() => {
    api("GET", "/business-contacts")
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        setCompanies(arr);
        if (arr.length) setSel(arr[0].company);
      })
      .catch(() => toast.error("Could not load business contacts"))
      .finally(() => setLoading(false));
  }, []);

  const company = useMemo(() => companies.find((c) => c.company === sel) || null, [companies, sel]);
  const contacts = company?.contacts || [];
  const current = contacts.find((p) => p.id === contactId) || null;

  // "Find by first name" across all companies
  const allNames = useMemo(() => {
    const out = [];
    companies.forEach((c) => (c.contacts || []).forEach((p) => out.push({ company: c.company, ...p })));
    return out.sort((a, b) => (a.first_name || "").localeCompare(b.first_name || ""));
  }, [companies]);

  const jumpToName = (key) => {
    const [comp, id] = key.split("||");
    setSel(comp); setContactId(id);
  };

  const soon = () => toast("Add / Edit / Delete — coming soon");

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/55 backdrop-blur-sm flex items-start justify-center p-3 sm:p-6 overflow-y-auto" data-testid="overlay-business-contacts">
      <div className="relative w-full max-w-6xl my-2 rounded-lg overflow-hidden shadow-2xl border border-sky-300 bg-gradient-to-b from-sky-100 to-sky-200">
        {/* title bar */}
        <div className="flex items-center justify-between bg-[#2a9fd6] text-white px-3 py-1.5">
          <div className="flex items-center gap-2 text-[13px] font-semibold"><Users className="h-4 w-4" /> Business Contacts</div>
          <button onClick={onClose} data-testid="business-contacts-close-x" title="Close" className="h-6 w-6 rounded-sm hover:bg-white/20 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-4">
          {/* header controls */}
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-white shadow flex items-center justify-center shrink-0"><Users className="h-5 w-5 text-sky-500" /></div>
              <h2 className="text-xl font-display font-bold text-slate-700">Business Contacts</h2>
            </div>
            <label className="flex flex-col gap-0.5">
              <span className="text-[11px] font-semibold text-slate-600">Find Business:</span>
              <select data-testid="bc-find-business" value={sel} onChange={(e) => { setSel(e.target.value); setContactId(null); }}
                className="min-w-[240px] rounded-sm border border-slate-400 bg-white px-2 py-1.5 text-[13px] text-slate-900">
                {companies.map((c) => <option key={c.company} value={c.company}>{c.company}</option>)}
              </select>
            </label>
            <button onClick={soon} data-testid="bc-add-company" className="rounded-sm border border-slate-400 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50">Add A New Company</button>
            <label className="flex flex-col gap-0.5 ml-auto">
              <span className="text-[11px] font-semibold text-slate-600">Find By First Name:</span>
              <select data-testid="bc-find-name" value="" onChange={(e) => e.target.value && jumpToName(e.target.value)}
                className="min-w-[220px] rounded-sm border border-slate-400 bg-white px-2 py-1.5 text-[13px] text-slate-900">
                <option value="">— select a person —</option>
                {allNames.map((p) => <option key={`${p.company}||${p.id}`} value={`${p.company}||${p.id}`}>{p.name || "(no name)"} — {p.company}</option>)}
              </select>
            </label>
            <button onClick={onClose} data-testid="bc-save-close" className="rounded-sm border border-slate-400 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50">Save and Close Form</button>
          </div>

          {loading ? (
            <div className="h-64 flex items-center justify-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
              {/* left: company + contact grid */}
              <div className="rounded-sm border border-slate-300 bg-white/70">
                <div className="flex flex-col gap-1 p-2 border-b border-slate-200">
                  <button onClick={soon} data-testid="bc-edit-company" className="w-full rounded-sm border border-slate-400 bg-white py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50">Add/Edit/Delete This Company</button>
                  <button onClick={soon} data-testid="bc-edit-contacts" className="w-full rounded-sm border border-slate-400 bg-white py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50">Add/Edit Individual Contact Details For This Company</button>
                </div>
                <div className="px-3 py-2 text-lg font-display font-bold text-slate-800">{company?.company}</div>
                <div className="relative mb-1 px-2">
                  <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} placeholder="Filter contacts…" data-testid="bc-filter-contacts"
                    className="w-full rounded-sm border border-slate-300 bg-white pl-8 pr-2 py-1 text-[12px]" />
                </div>
                <div className="max-h-[52vh] overflow-y-auto">
                  <table className="w-full text-[12.5px]">
                    <thead className="sticky top-0 bg-slate-100 text-slate-600">
                      <tr>
                        <th className="text-left font-semibold px-3 py-1.5 border-b border-slate-200">Name</th>
                        <th className="text-left font-semibold px-2 py-1.5 border-b border-slate-200">Position</th>
                        <th className="text-left font-semibold px-2 py-1.5 border-b border-slate-200">Department</th>
                        <th className="px-2 py-1.5 border-b border-slate-200"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.filter((p) => !nameFilter || (`${p.name} ${p.position} ${p.department}`.toLowerCase().includes(nameFilter.toLowerCase()))).map((p) => (
                        <tr key={p.id} data-testid={`bc-row-${p.id}`} className={`border-b border-slate-100 ${current?.id === p.id ? "bg-sky-50" : "hover:bg-slate-50"}`}>
                          <td className="px-3 py-1.5 text-slate-900">{p.name || <span className="text-slate-300">—</span>}</td>
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[180px]" title={p.position}>{p.position}</td>
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[180px]" title={p.department}>{p.department}</td>
                          <td className="px-2 py-1.5 text-right">
                            <button onClick={() => setContactId(p.id)} data-testid={`bc-view-${p.id}`} className="inline-flex items-center gap-1 rounded-sm border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-50">View Details <ChevronRight className="h-3 w-3" /></button>
                          </td>
                        </tr>
                      ))}
                      {contacts.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">No contacts on file for this company.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* right: selected contact detail */}
              <div className="rounded-sm border border-slate-300 bg-white/70 p-4 min-h-[16rem]">
                {current ? (
                  <div className="flex flex-col gap-3" data-testid="bc-detail">
                    <div className="text-center border-b border-slate-200 pb-2">
                      <div className="font-display font-bold text-slate-800 underline underline-offset-2">
                        {current.name}{current.position ? ` - ${current.position}` : ""}{current.department ? ` - ${current.department} Department` : ""}
                      </div>
                    </div>
                    {current.email && (
                      <a href={`mailto:${current.email}`} data-testid="bc-send-email" className="mx-auto inline-flex items-center gap-2 rounded-sm border border-slate-400 bg-white px-4 py-1.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"><Mail className="h-4 w-4" /> Send Email</a>
                    )}
                    {current.numbers.filter((n) => (n.type || "").toLowerCase() !== "email").map((n, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-24 shrink-0 text-right text-[12px] font-semibold text-slate-600 inline-flex items-center justify-end gap-1"><Phone className="h-3.5 w-3.5" />{n.type} :</div>
                        <div className="flex-1 rounded-sm border border-slate-300 bg-white px-2 py-1 text-[13px] font-semibold text-slate-900">{n.value}</div>
                      </div>
                    ))}
                    {current.email && (
                      <div className="flex items-center gap-2">
                        <div className="w-24 shrink-0 text-right text-[12px] font-semibold text-slate-600 inline-flex items-center justify-end gap-1"><Mail className="h-3.5 w-3.5" />Email :</div>
                        <div className="flex-1 rounded-sm border border-slate-300 bg-white px-2 py-1 text-[13px] font-semibold text-slate-900 break-all">{current.email}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-center text-slate-400 text-sm">Select a contact and press <span className="font-semibold mx-1">View Details</span> to see their phone &amp; email.</div>
                )}
              </div>
            </div>
          )}

          <button onClick={onClose} data-testid="business-contacts-close" className="mt-4 w-full rounded-md bg-slate-900 hover:bg-slate-700 text-white font-bold py-2.5">Close</button>
        </div>
      </div>
    </div>
  );
}
